const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
/** Minimum content height (mm) to justify a new page; avoids a sliver on an extra page */
const MIN_PAGE_CONTENT_MM = 5;
const CANVAS_SCALE = 2;
const JPEG_QUALITY = 0.9;

interface PdfDoc {
  addPage(): void;
  addImage(imgData: string, format: string, x: number, y: number, w: number, h: number): void;
}

/**
 * Renders a section's canvas to the PDF, adding new pages as needed when content
 * is taller than one A4 page.
 */
function addSectionToPdf(
  pdf: PdfDoc,
  imgData: string,
  imgWidthMm: number,
  imgHeightMm: number,
  startNewPage: boolean,
): void {
  if (startNewPage) {
    pdf.addPage();
  }

  let yOffset = 0;
  while (yOffset < imgHeightMm) {
    const remainingMm = imgHeightMm - yOffset;
    if (yOffset > 0) {
      if (remainingMm < MIN_PAGE_CONTENT_MM) {
        break;
      }
      pdf.addPage();
    }
    pdf.addImage(imgData, 'JPEG', 0, -yOffset, imgWidthMm, imgHeightMm);
    yOffset += A4_HEIGHT_MM;
  }
}

export async function exportToPdf(
  reportElement: HTMLElement,
  propertyName: string,
  analysisDate: string,
): Promise<void> {
  const { default: html2canvas } = await import('html2canvas');
  const { default: jsPDF } = await import('jspdf');

  const root = reportElement.firstElementChild ?? reportElement;
  const sections = root.querySelectorAll<HTMLElement>('[data-pdf-section]');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgWidthMm = A4_WIDTH_MM;

  if (sections.length > 0) {
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const canvas = await html2canvas(section, { scale: CANVAS_SCALE, useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;
      const startNewPage = section.hasAttribute('data-pdf-page-break-before');

      addSectionToPdf(pdf, imgData, imgWidthMm, imgHeightMm, startNewPage);
    }
  } else {
    const canvas = await html2canvas(reportElement, { scale: CANVAS_SCALE, useCORS: true });
    const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;
    addSectionToPdf(pdf, imgData, imgWidthMm, imgHeightMm, false);
  }

  const safeName = propertyName.replace(/[^a-zA-Z0-9]/g, '_');
  pdf.save(`parity_report_${safeName}_${analysisDate}.pdf`);
}
